import type {
  ApplyTaskProposalRequest,
  ConversationDetail,
  TaskPerson,
  TaskProposalView,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, Clock, ListTodo, X } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { csrf } from "../account/queries.ts";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { Link, projectTaskPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { AssigneePicker } from "./AssigneePicker.tsx";
import { calendarDate } from "./parts.tsx";
import { taskKeys } from "./queries.ts";

/**
 * A task change the assistant proposed under an answer (openspec change `agent-turn`, D11, ADR
 * 0016 amended): the exact fields that would be written, and Create (or Apply), Edit and Dismiss.
 * Nothing changes without the user's click, which runs as them at that moment: a role lost since
 * the answer refuses it. Dashed while undecided, like the memory and question cards; decided, it
 * settles into one quiet line. Shown to the conversation's creator only (the server sends it to
 * nobody else).
 */
const proposalRoute =
  api.api.projects[":projectId"].conversations[":conversationId"]["task-proposals"][":proposalId"];

function useTaskProposalDecision(
  projectId: string,
  conversationId: string,
  proposal: TaskProposalView,
  onSettled: (view: TaskProposalView) => void,
) {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const param = { projectId, conversationId, proposalId: proposal.id };
  // A cached conversation shows the card decided when it is reopened.
  const settle = (view: TaskProposalView) => {
    queryClient.setQueriesData<ConversationDetail>({ queryKey: ["conversation"] }, (detail) =>
      detail?.taskProposals
        ? {
            ...detail,
            taskProposals: detail.taskProposals.map((p) => (p.id === view.id ? view : p)),
          }
        : detail,
    );
    onSettled(view);
  };
  return {
    apply: useMutation({
      mutationFn: async (edits: ApplyTaskProposalRequest): Promise<TaskProposalView> =>
        ok(await proposalRoute.apply.$post({ param, json: edits }, csrf(csrfToken))),
      onSuccess: (view) => {
        settle(view);
        return queryClient.invalidateQueries({ queryKey: taskKeys.all });
      },
    }),
    dismiss: useMutation({
      mutationFn: async (): Promise<TaskProposalView> =>
        ok(await proposalRoute.dismiss.$post({ param }, csrf(csrfToken))),
      onSuccess: settle,
    }),
  };
}

export function TaskProposalCard(props: {
  projectId: string;
  conversationId: string;
  proposal: TaskProposalView;
  onSettled?: (view: TaskProposalView) => void;
}) {
  const { projectId, conversationId } = props;
  const { t, i18n } = useTranslation();
  const id = useId();
  const [view, setView] = useState(props.proposal);
  const { apply, dismiss } = useTaskProposalDecision(projectId, conversationId, view, (next) => {
    setView(next);
    props.onSettled?.(next);
  });
  const { payload } = view;
  const fields = payload.fields;
  const create = payload.kind === "create";
  const proposedAssignee = (fields.assignee ?? null) as TaskPerson | null;
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(
    create ? payload.fields.title : (payload.fields.title ?? payload.fields.taskTitle),
  );
  const [assignee, setAssignee] = useState<TaskPerson | null>(proposedAssignee);
  const [due, setDue] = useState(fields.dueDate ?? "");
  const busy = apply.isPending || dismiss.isPending;
  // The inline edit covers the title, assignee and due date an update proposes (all for a create).
  const editable =
    create ||
    payload.fields.title !== undefined ||
    payload.fields.assignee !== undefined ||
    payload.fields.dueDate !== undefined;
  const error = apply.error ?? dismiss.error;
  const day = (value: string) => (
    <time dateTime={value} className="tabular-nums">
      {calendarDate(value, i18n.language)}
    </time>
  );

  if (view.status !== "pending") {
    const applied = view.status === "applied";
    const Icon = applied ? CircleCheck : view.status === "expired" ? Clock : X;
    return (
      <p
        role="status"
        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted"
        data-proposal-status={view.status}
        data-esi-content="tasks"
      >
        <Icon aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        <span>
          {applied
            ? create
              ? t("taskProposal.created")
              : t("taskProposal.applied")
            : view.status === "expired"
              ? t("taskProposal.expired")
              : t("taskProposal.dismissed")}
        </span>
        {applied && view.taskId ? (
          <Link
            href={projectTaskPath(projectId, view.taskId)}
            className="text-accent-text underline-offset-4 hover:underline"
          >
            {t("taskProposal.open")}
          </Link>
        ) : null}
      </p>
    );
  }

  const edits = (): ApplyTaskProposalRequest => {
    if (!editing) {
      return {};
    }
    // Only the fields the edit shows (an update edits what it proposes), and only when changed.
    const original = payload.fields.title;
    const trimmed = title.trim();
    const shows = (field: "title" | "dueDate" | "assignee") =>
      create || payload.fields[field] !== undefined;
    return {
      ...(shows("title") &&
        original !== undefined &&
        trimmed !== "" &&
        trimmed !== original && { title: trimmed }),
      ...(shows("dueDate") &&
        (due || null) !== (fields.dueDate ?? null) && { dueDate: due || null }),
      ...(shows("assignee") &&
        (assignee?.userId ?? null) !== (proposedAssignee?.userId ?? null) && {
          assigneeUserId: assignee?.userId ?? null,
        }),
    };
  };

  const rows: { label: string; value: ReactNode }[] = create
    ? [
        { label: t("taskProposal.fields.title"), value: payload.fields.title },
        {
          label: t("taskProposal.fields.assignee"),
          value: proposedAssignee?.name ?? t("tasks.unassigned"),
        },
        {
          label: t("taskProposal.fields.due"),
          value: payload.fields.dueDate ? day(payload.fields.dueDate) : t("tasks.noDue"),
        },
        ...(payload.fields.description
          ? [{ label: t("taskProposal.fields.description"), value: payload.fields.description }]
          : []),
      ]
    : [
        ...(payload.fields.title !== undefined
          ? [{ label: t("taskProposal.fields.title"), value: payload.fields.title }]
          : []),
        ...(payload.fields.status !== undefined
          ? [
              {
                label: t("taskProposal.fields.status"),
                value: t(`tasks.${payload.fields.status}`),
              },
            ]
          : []),
        ...(payload.fields.assignee !== undefined
          ? [
              {
                label: t("taskProposal.fields.assignee"),
                value: payload.fields.assignee?.name ?? t("tasks.unassigned"),
              },
            ]
          : []),
        ...(payload.fields.dueDate !== undefined
          ? [
              {
                label: t("taskProposal.fields.due"),
                value: payload.fields.dueDate ? day(payload.fields.dueDate) : t("tasks.noDue"),
              },
            ]
          : []),
        ...(payload.fields.description !== undefined
          ? [
              {
                label: t("taskProposal.fields.description"),
                value: payload.fields.description ?? t("taskProposal.cleared"),
              },
            ]
          : []),
        ...(payload.fields.comment !== undefined
          ? [{ label: t("taskProposal.fields.comment"), value: payload.fields.comment }]
          : []),
      ];

  return (
    <section
      aria-labelledby={`${id}-title`}
      data-task-proposal={view.id}
      data-esi-content="tasks"
      className={cn(
        // Dashed while undecided, editing included: the card's anatomy never changes.
        "flex flex-col gap-2.5 rounded-lg border border-dashed border-line-strong px-3.5 py-3",
      )}
    >
      <div className="flex items-start gap-2.5">
        <ListTodo
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-fg-muted"
          strokeWidth={1.75}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <h3 id={`${id}-title`} className="text-[13px] font-medium text-fg-secondary">
            {create
              ? t("taskProposal.createTitle")
              : t("taskProposal.updateTitle", { title: payload.fields.taskTitle })}
          </h3>
          {editing ? (
            // Stacked like the task editor: the assignee search brings its own field.
            <div className="flex max-w-xl flex-col gap-3 pt-1">
              {create || payload.fields.title !== undefined ? (
                <div className="flex flex-col gap-1.5">
                  <label htmlFor={`${id}-t`} className="text-sm font-medium">
                    {t("taskProposal.fields.title")}
                  </label>
                  <Input
                    id={`${id}-t`}
                    value={title}
                    maxLength={200}
                    autoFocus
                    onChange={(e) => {
                      apply.reset();
                      setTitle(e.currentTarget.value);
                    }}
                  />
                </div>
              ) : null}
              {create || payload.fields.assignee !== undefined ? (
                <div className="flex flex-col gap-1.5">
                  <p className="text-sm font-medium">{t("taskProposal.fields.assignee")}</p>
                  <AssigneePicker
                    projectId={projectId}
                    {...(view.taskId && { taskId: view.taskId })}
                    value={assignee}
                    onChange={(person) => {
                      apply.reset();
                      setAssignee(person);
                    }}
                    disabled={busy}
                  />
                </div>
              ) : null}
              {create || payload.fields.dueDate !== undefined ? (
                <div className="flex flex-col gap-1.5">
                  <label htmlFor={`${id}-d`} className="text-sm font-medium">
                    {t("taskProposal.fields.due")}
                  </label>
                  <Input
                    id={`${id}-d`}
                    type="date"
                    value={due}
                    className="w-44 tabular-nums"
                    onChange={(e) => {
                      apply.reset();
                      setDue(e.currentTarget.value);
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[13px]">
              {rows.map((row) => (
                <div key={row.label} className="contents">
                  <dt className="text-fg-muted">{row.label}</dt>
                  <dd className="min-w-0 break-words text-fg whitespace-pre-line line-clamp-4">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {view.sourceCount > 0 || view.trust === "external" ? (
            <p className="text-xs text-fg-muted">
              {t("taskProposal.fromSources", { count: view.sourceCount })}
            </p>
          ) : null}
        </div>
      </div>
      {error ? (
        <InlineError>
          {!(error instanceof ApiError)
            ? t("errors.network")
            : error.status === 403
              ? t("taskProposal.refused")
              : error.status === 404
                ? t("taskProposal.gone")
                : error.code === "proposal_expired"
                  ? t("taskProposal.expired")
                  : error.code === "proposal_decided"
                    ? t("taskProposal.decided")
                    : error.status === 422
                      ? t("taskProposal.invalidAssignee")
                      : error.message}
        </InlineError>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5 pl-6.5">
        <Button
          type="button"
          size="sm"
          disabled={busy || (editing && title.trim() === "")}
          onClick={() => apply.mutate(edits())}
        >
          {apply.isPending
            ? t("taskProposal.applying")
            : create
              ? t("taskProposal.create")
              : t("taskProposal.apply")}
        </Button>
        {editing ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              setEditing(false);
              setTitle(
                create ? payload.fields.title : (payload.fields.title ?? payload.fields.taskTitle),
              );
              setAssignee(proposedAssignee);
              setDue(fields.dueDate ?? "");
              apply.reset();
            }}
          >
            {t("taskProposal.cancel")}
          </Button>
        ) : editable ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => setEditing(true)}
          >
            {t("taskProposal.edit")}
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          className="text-fg-secondary"
          onClick={() => dismiss.mutate()}
        >
          <X aria-hidden="true" strokeWidth={1.75} />
          {t("taskProposal.dismiss")}
        </Button>
      </div>
    </section>
  );
}
